import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { CronofyOptions } from '../../../../components/cronofy/CronofyOptions';
import { InterviewStage } from '../../../../entities/applicant_tracking/InterviewStage';
import { EventSlot } from '../../../../components/cronofy/CronofyDatePicker/EventSlot';
import { InterviewDetails } from './InterviewDetails';
import { InterviewDatePicker } from './InterviewDatePicker';
import { User } from '../../../../entities/User';
import { Candidate } from '../../../../entities/applicant_tracking/Candidate';
import { ApplicationService } from '../../../../services/applicant_tracking/ApplicationService';
import { InterviewListItem } from '../../../../entities/InterviewListItem';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { VcsProvider } from '../../../../enums/IntegrationProvider/VcsProvider';
import { Interview } from '../../../../entities/applicant_tracking/Interview';
import { InterviewerParticipant } from '../../../../entities/applicant_tracking/InterviewerParticipant';
import {
  mapIntToScheduleFrom,
  ScheduleFromType,
} from './ScheduleFromSelect/ScheduleFromTypes';
import { DEFAULT_DURATION_IN_MINUTES } from './InterviewDetails/InterviewDuration';
import { DEFAULT_DATE_RANGE } from '../../../../components/DateRangeSelect';
import { DEFAULT_SCHEDULE_FROM_HOURS } from './ScheduleFromSelect';
import {
  getAvailableMeetingProviders,
  getDefaultMeetingProvider,
} from '../../../../utils/applicant_tracking/interviews/interviews';
import { CalendarEventPanel } from './CalendarEventPanel';
import { EmailTemplate } from '../../../../entities/EmailTemplate';
import { UpsertEmailTemplateDto } from '../../../EmailTemplates/SetupEmailTemplatePage/UpsertEmailTemplateDto';
import { InterviewTemplate } from '../../../../entities/v1/applicant_tracking/InterviewTemplate';
import { userToParticipantMapper } from './utils/userToParticipantMapper';
import { InterviewTemplateService } from '../../../../services/v1/applicant_tracking/InterviewTemplateService';
import { AlertObject, AlertType } from '../../../../components/Alert';
import { DefaultAlert } from '../../../../components/DefaultAlert';

interface PropTypes {
  meetingOrganizer: User;
  applicationIds?: number[];
  totalApplications?: number;
  interview?: Interview;
  title: string;
  cronofyDatePickerOptions: CronofyOptions;
  candidate?: Candidate;
  participants: User[];
  interviewStage: InterviewStage;
  meetingProviders: VcsProvider[];
  meetingProvider: VcsProvider;
  interviewTemplateId?: number | null;
  durationMinutes?: number;
  customImage?: string;
  description?: string;
  candidateView: boolean;
  selfScheduleView: boolean;
  jobName?: string;
  startDate?: Date;
  scheduleFromHours?: number;
  dateRange?: number;
  timezone?: string;
  interviewDatePickerFooter?: React.ReactNode;
  onConfirm: (
    eventSlot: EventSlot,
    participants?: InterviewerParticipant[],
    meetingProvider?: string,
    calendarEventTemplateId?: number,
    calendarEventTemplateDto?: UpsertEmailTemplateDto,
  ) => void;
  allowChangingMeetingOrganizer: boolean;
  allowIgnoreParticipantCalendar: boolean;
  personalizingSchedulingInvitesV1FfEnabled?: boolean;
  setSchedulingSettings?: (schedulingSettings: SchedulingSettings) => void;
}

export interface SchedulingSettings {
  participants: InterviewerParticipant[];
  durationMinutes: number;
  meetingProvider: VcsProvider;
  dateRange: number;
  scheduleFrom: ScheduleFromType;
}

function getInterviewTemplate(id: number): Promise<InterviewTemplate> {
  return InterviewTemplateService.get(id, {
    expand: [
      'participant_users',
      'participant_users.scheduling_user_integration',
      'participant_users.scheduling_user_integration.availabilities',
    ],
    includeFields: ['participant_users.has_calendar_connected'],
  });
}

export default function ScheduleInterviewPage(props: PropTypes) {
  const [participants, setParticipants] = useState<InterviewerParticipant[]>(
    userToParticipantMapper(props.participants, props.meetingOrganizer.id),
  );
  const [durationMinutes, setDurationMinutes] = useState<number>(
    props.durationMinutes || DEFAULT_DURATION_IN_MINUTES,
  );

  const [meetingProvider, setMeetingProvider] = useState<VcsProvider>(
    props.meetingProvider,
  );
  const [meetingProviders, setMeetingProviders] = useState<VcsProvider[]>(
    props.meetingProviders,
  );

  const [calendarEventTemplate, setCalendarEventTemplate] =
    useState<EmailTemplate>(null);
  const [isEditingCalendarEventTemplate, setIsEditingCalendarEventTemplate] =
    useState<boolean>(false);
  const [calendarEventTemplateDto, setCalendarEventTemplateDto] =
    useState<UpsertEmailTemplateDto>(null);
  const [interviewTemplate, setInterviewTemplate] =
    useState<InterviewTemplate>(undefined);
  const [alert, setAlert] = useState<AlertObject>(null);

  const [dateRange, setDateRange] = useState<number>(
    props.dateRange || DEFAULT_DATE_RANGE,
  );

  const [scheduleFrom, setScheduleFrom] = useState<ScheduleFromType>(
    mapIntToScheduleFrom(
      props.scheduleFromHours || DEFAULT_SCHEDULE_FROM_HOURS,
    ),
  );

  const onChangingMeetingOrganizer = (participant: InterviewerParticipant) => {
    setMeetingProvider(getDefaultMeetingProvider(participant.user));
    setMeetingProviders(
      getAvailableMeetingProviders(participant.user, meetingProviders),
    );
  };

  const [applicationInterviews, setApplicationInterviews] =
    useState<InterviewListItem[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (!props.candidateView) {
    useEffect(() => {
      (async () => {
        if (props.applicationIds?.length != 1) {
          return;
        }

        const applicationId = props.applicationIds[0];

        if (applicationInterviews == null) {
          setIsLoading(true);

          ApplicationService.listInterviews(
            applicationId,
            'scheduled',
            new Date(),
          )
            .then(setApplicationInterviews)
            .catch((e) => {
              console.error(e);
            })
            .finally(() => setIsLoading(false));
        }
      })();
    }, [props.applicationIds]);
  }

  useEffect(() => {
    if (props.setSchedulingSettings) {
      props.setSchedulingSettings({
        participants: participants,
        durationMinutes: durationMinutes,
        meetingProvider: meetingProvider,
        dateRange: dateRange,
        scheduleFrom: scheduleFrom,
      });
    }
  }, [participants, durationMinutes, meetingProvider, dateRange, scheduleFrom]);

  useEffect(() => {
    if (interviewTemplate) {
      setIsLoading(true);
      getInterviewTemplate(interviewTemplate.id)
        .then((template) => {
          setParticipants(
            userToParticipantMapper(
              template.participantUsers,
              template.meetingOrganizerUserId,
            ),
          );
          setMeetingProvider(template.meetingProvider);
          setDateRange(template.dateRange);
          setScheduleFrom(mapIntToScheduleFrom(template.scheduleFromHours));
          setDurationMinutes(template.meetingDuration);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [interviewTemplate]);

  return (
    <Row className={'gx-0'}>
      <Col lg={4} className={classNames(styles['col-wrapper'], 'me-4')}>
        <DefaultAlert alert={alert} setAlert={setAlert} />
        <InterviewDetails
          title={props.title}
          description={props.description}
          applicationIds={props.applicationIds}
          totalApplications={props.totalApplications}
          candidate={props.candidate}
          participants={participants}
          setParticipants={setParticipants}
          durationMinutes={durationMinutes}
          setDurationMinutes={setDurationMinutes}
          meetingProvider={meetingProvider}
          setMeetingProvider={setMeetingProvider}
          meetingProviders={meetingProviders}
          interviewTemplate={interviewTemplate}
          setInterviewTemplate={setInterviewTemplate}
          defaultInterviewTemplateId={props.interviewTemplateId}
          customImage={props.customImage}
          candidateView={props.candidateView}
          onChangingMeetingOrganizer={onChangingMeetingOrganizer}
          allowChangingMeetingOrganizer={props.allowChangingMeetingOrganizer}
          allowIgnoreParticipantCalendar={props.allowIgnoreParticipantCalendar}
          calendarEventTemplate={calendarEventTemplate}
          isEditingCalendarEventTemplate={isEditingCalendarEventTemplate}
          setCalendarEventTemplate={setCalendarEventTemplate}
          setIsEditingCalendarEventTemplate={setIsEditingCalendarEventTemplate}
          personalizingSchedulingInvitesV1FfEnabled={
            props.personalizingSchedulingInvitesV1FfEnabled
          }
          emailTemplateId={props.interview?.emailTemplateId}
        />
      </Col>
      <Col className={classNames(styles['col-wrapper'], 'p-4 hide-scrollbar')}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {isEditingCalendarEventTemplate ? (
              <CalendarEventPanel
                referenceTemplate={calendarEventTemplate}
                calendarEventTemplateDto={calendarEventTemplateDto}
                onCancel={() => setIsEditingCalendarEventTemplate(false)}
                onSubmit={(dto) => {
                  setAlert({
                    type: AlertType.Success,
                    message: 'The calendar event was successfully updated!',
                  });
                  setCalendarEventTemplateDto(dto);
                  setIsEditingCalendarEventTemplate(false);
                }}
                setReferenceTemplate={setCalendarEventTemplate}
              />
            ) : (
              <InterviewDatePicker
                cronofyDatePickerOptions={props.cronofyDatePickerOptions}
                applicationInterviews={applicationInterviews}
                interview={props.interview}
                participants={participants}
                meetingProvider={meetingProvider}
                durationMinutes={durationMinutes}
                interviewStage={props.interviewStage}
                onConfirm={props.onConfirm}
                candidateView={props.candidateView}
                selfScheduleView={props.selfScheduleView}
                jobName={props.jobName}
                startDate={props.startDate}
                scheduleFrom={scheduleFrom}
                setScheduleFrom={setScheduleFrom}
                dateRange={dateRange}
                setDateRange={setDateRange}
                timezone={props.timezone}
                calendarEventTemplateId={calendarEventTemplate?.id}
                calendarEventTemplateDto={calendarEventTemplateDto}
                interviewDatePickerFooter={props.interviewDatePickerFooter}
              />
            )}
          </>
        )}
      </Col>
    </Row>
  );
}
